export type VisibleRange = {
  min: number;
  max: number;
};

export type DecimationMode = 'peaks' | 'min-max';

export const toTimestamp = (x: unknown): number => {
  if (typeof x === 'number') {
    return x;
  }
  if (x instanceof Date) {
    return x.getTime();
  }
  if (typeof x === 'string') {
    return Date.parse(x);
  }
  return NaN;
};

const getY = (point: unknown): number => {
  const y = (point as { y?: unknown } | null | undefined)?.y;
  if (typeof y === 'number') {
    return y;
  }
  if (Array.isArray(y) && y.length === 2) {
    return (Number(y[0]) + Number(y[1])) / 2;
  }
  return NaN;
};

const getX = (point: unknown): number =>
  toTimestamp((point as { x?: unknown } | null | undefined)?.x);

export const filterByRange = <T>(
  points: T[],
  range: VisibleRange | null,
): T[] => {
  if (!range) {
    return points;
  }
  return points.filter((point) => {
    const x = getX(point);
    return x >= range.min && x <= range.max;
  });
};

interface Bucket {
  minPoint: unknown;
  minY: number;
  minOrder: number;
  maxPoint: unknown;
  maxY: number;
  maxOrder: number;
}

const withX = (point: unknown, x: number): unknown => ({
  ...(point as object),
  x,
});

const aggregateByBucket = (
  points: unknown[],
  width: number,
  mode: DecimationMode,
): unknown[] => {
  const buckets = new Map<number, Bucket>();

  points.forEach((point, order) => {
    const x = getX(point);
    const y = getY(point);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return;
    }
    // El intervalo se ancla al instante 0 (no a la ventana), así no "baila" al desplazarse.
    const key = Math.floor(x / width);
    const bucket = buckets.get(key);
    if (!bucket) {
      buckets.set(key, {
        minPoint: point,
        minY: y,
        minOrder: order,
        maxPoint: point,
        maxY: y,
        maxOrder: order,
      });
      return;
    }
    if (y < bucket.minY) {
      bucket.minPoint = point;
      bucket.minY = y;
      bucket.minOrder = order;
    }
    if (y > bucket.maxY) {
      bucket.maxPoint = point;
      bucket.maxY = y;
      bucket.maxOrder = order;
    }
  });

  const result: unknown[] = [];
  [...buckets.keys()]
    .sort((a, b) => a - b)
    .forEach((key) => {
      const bucket = buckets.get(key)!;
      const start = key * width;

      if (mode === 'peaks') {
        result.push(withX(bucket.maxPoint, start + width / 2));
        return;
      }
      // Posiciones fijas dentro del intervalo (25 % y 75 %): iguales para todas las series.
      if (bucket.minOrder === bucket.maxOrder) {
        result.push(withX(bucket.maxPoint, start + width * 0.25));
        return;
      }
      const [first, second] =
        bucket.minOrder < bucket.maxOrder
          ? [bucket.minPoint, bucket.maxPoint]
          : [bucket.maxPoint, bucket.minPoint];
      result.push(
        withX(first, start + width * 0.25),
        withX(second, start + width * 0.75),
      );
    });

  return result;
};

export const decimateDatasetsByTime = <T extends { data: unknown[] }>(
  datasets: T[],
  maxPoints: number,
  range: VisibleRange | null,
  mode: DecimationMode = 'min-max',
): T[] => {
  const filtered = datasets.map((dataset) =>
    filterByRange(dataset.data, range),
  );
  const untouched = () =>
    datasets.map((dataset, i) => ({ ...dataset, data: filtered[i] }));

  if (
    maxPoints <= 0 ||
    filtered.every((points) => points.length <= maxPoints)
  ) {
    return untouched();
  }

  let windowMin = range?.min ?? Number.POSITIVE_INFINITY;
  let windowMax = range?.max ?? Number.NEGATIVE_INFINITY;
  if (!range) {
    filtered.forEach((points) =>
      points.forEach((point) => {
        const x = getX(point);
        if (Number.isFinite(x)) {
          windowMin = Math.min(windowMin, x);
          windowMax = Math.max(windowMax, x);
        }
      }),
    );
  }

  const slotsPerBucket = mode === 'min-max' ? 2 : 1;
  const bucketCount = Math.max(1, Math.floor(maxPoints / slotsPerBucket));
  const width = Math.max(1, Math.round((windowMax - windowMin) / bucketCount));
  if (!Number.isFinite(width)) {
    return untouched();
  }

  return datasets.map((dataset, i) => ({
    ...dataset,
    data: aggregateByBucket(filtered[i], width, mode),
  }));
};
